/**
 * BetaBadgeView — the "BETA" pill that marks a surface as still stabilising
 * (SE-8200).
 *
 * ## Why this exists as a component rather than three copies
 *
 * The markup was inline in `LeftSidebarView`, which was fine while the left
 * sidebar was the only thing that wore it. SE-8200 puts the same badge on the
 * Squad **Compliance** tab and the Scouting **Matches** tab, and "the same
 * badge" is a claim that copies cannot keep true — the sidebar's pill and a
 * tab's pill would drift the first time either was restyled, and nothing would
 * fail.
 *
 * So the classes live here once and the three call sites render this. The
 * values are unchanged from what the sidebar already shipped: this is an
 * extraction, not a redesign.
 *
 * ## The tokens
 *
 * `rounded-none` is deliberate and matches the sidebar — a squared pill beside
 * a tab label, not a rounded chip that would read as a count.
 * `text-needs-semantic-info-700` is the tracked semantic-debt tier, carried
 * over as-is: promoting it to a real semantic token is a design-system
 * decision, and inventing one here to look tidy would spend a slot from a tier
 * that is full (35/35).
 * @module components/molecules/BetaBadge/BetaBadgeView
 */
import React, { memo } from "react";

import { Badge, BadgeText } from "@/components/atoms/Badge";

/** Props for BetaBadgeView. */
export interface BetaBadgeViewProps {
  /**
   * The word on the pill.
   *
   * Defaults to `BETA`. Overridable because the left sidebar drives its text
   * from each nav item's `badgeText`, which is a free string — hardcoding the
   * word here would make this unusable for the one surface that already had a
   * badge.
   */
  readonly label?: string;
  /** Test identity. */
  readonly testID?: string;
}

/**
 * Renders the beta pill.
 * @param root0 - Component props
 * @param root0.label - The word on the pill, default "BETA"
 * @param root0.testID - Test identity
 * @returns The badge element.
 */
const BetaBadgeView = ({ label = "BETA", testID }: BetaBadgeViewProps) => (
  <Badge
    testID={testID}
    className="rounded-none bg-surface-info-subtle px-2 py-micro"
  >
    <BadgeText className="font-heading text-micro text-needs-semantic-info-700">
      {label}
    </BadgeText>
  </Badge>
);

BetaBadgeView.displayName = "BetaBadgeView";

export default memo(BetaBadgeView);
